import React from 'react';
import { CaptionAlign, SlideState, TextStyleFields } from '../types';
import { estimateNarrationBox } from '../overlays';
import TextStyleToolbar from '../TextStyleToolbar';

export interface ContentTabProps {
  slide: SlideState;
  /** Clip slides defer their text to the Clip Editor, so the inputs are read-only here. */
  isClip: boolean;
  onOpenClipEditor: () => void;
  /** True while the short is being created on first hand-off. */
  openingClip?: boolean;
  updateActive: (patch: Partial<SlideState>) => void;
  setCaptionText: (text: string) => void;
  setCaptionAlign: (align: CaptionAlign) => void;
  setCaptionStyle: (patch: Partial<TextStyleFields>) => void;
  clearCaption: () => void;
  updateNarrationText: (text: string) => void;
  setNarrationAlign: (align: CaptionAlign) => void;
  setNarrationStyle: (patch: Partial<TextStyleFields>) => void;
}

const ContentTab: React.FC<ContentTabProps> = ({
  slide,
  isClip,
  onOpenClipEditor,
  openingClip,
  updateActive,
  setCaptionText,
  setCaptionAlign,
  setCaptionStyle,
  clearCaption,
  updateNarrationText,
  setNarrationAlign,
  setNarrationStyle,
}) => (
  <>
    {isClip && (
      <div className="se-tab-notice">
        <span>Text on a clip belongs to the Clip Editor — edit it there and it follows the clip.</span>
        <button className="se-btn-inline" onClick={onOpenClipEditor} disabled={openingClip}>
          {openingClip ? 'Opening…' : 'Open Clip Editor'}
        </button>
      </div>
    )}

    {/* Caption */}
    <div className="se-field">
      <div className="se-field-header">
        <label className="se-label">Caption</label>
        {!isClip && slide.captionOverlay?.text && (
          <div className="se-align-toolbar">
            <button className={`se-align-btn ${slide.captionOverlay.align === 'left' ? 'se-align-active' : ''}`} onClick={() => setCaptionAlign('left')} title="Left">◀</button>
            <button className={`se-align-btn ${slide.captionOverlay.align === 'center' ? 'se-align-active' : ''}`} onClick={() => setCaptionAlign('center')} title="Center">▣</button>
            <button className={`se-align-btn ${slide.captionOverlay.align === 'right' ? 'se-align-active' : ''}`} onClick={() => setCaptionAlign('right')} title="Right">▶</button>
            <button className="se-align-btn se-align-remove" onClick={clearCaption} title="Remove caption">✕</button>
          </div>
        )}
      </div>
      <input
        type="text"
        className="se-input"
        placeholder={isClip ? 'Set in the Clip Editor' : 'Enter caption text...'}
        value={slide.caption}
        onChange={e => setCaptionText(e.target.value)}
        maxLength={100}
        readOnly={isClip}
        disabled={isClip}
      />
      {!isClip && <span className="se-charcount">{slide.caption.length}/100</span>}
      {!isClip && <TextStyleToolbar fields={slide.captionOverlay || {}} onChange={setCaptionStyle} />}
    </div>

    {/* Narration Text - Auto-enable TTS */}
    <div className="se-field">
      <div className="se-field-header">
        <label className="se-label">Narration Text</label>
        {!isClip && slide.longDescriptionText.trim() && (
          <label className="se-tts-toggle se-tts-auto">
            <input
              type="checkbox"
              checked={slide.ttsEnabled}
              onChange={e => updateActive({ ttsEnabled: e.target.checked })}
            />
            <span>🔊 Text-to-Speech</span>
          </label>
        )}
      </div>
      <textarea
        className="se-textarea"
        placeholder={isClip ? 'Set in the Clip Editor' : 'Enter narration text for this slide...'}
        value={slide.longDescriptionText}
        onChange={e => updateNarrationText(e.target.value)}
        rows={3}
        readOnly={isClip}
        disabled={isClip}
      />
      {!isClip && (
        <>
          <label className={`se-checkbox-label ${!slide.longDescriptionText.trim() ? 'se-checkbox-label-disabled' : ''}`} style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={slide.showLongTextWithTts}
              disabled={!slide.longDescriptionText.trim()}
              onChange={e => updateActive({
                showLongTextWithTts: e.target.checked,
                narrationOverlay: e.target.checked
                  ? (slide.narrationOverlay || {
                      x: 50,
                      y: 68,
                      align: 'center',
                      ...estimateNarrationBox(slide.longDescriptionText),
                    })
                  : slide.narrationOverlay,
              })}
            />
            <span>Show narration text on video while TTS plays</span>
          </label>
          {slide.showLongTextWithTts && slide.longDescriptionText.trim() && (
            <div className="se-field-header" style={{ marginTop: 4 }}>
              <span className="se-hint-text">Drag and pull in preview to position and size narration text background</span>
              <div className="se-align-toolbar">
                <button
                  className={`se-align-btn ${(slide.narrationOverlay?.align || 'center') === 'left' ? 'se-align-active' : ''}`}
                  onClick={() => setNarrationAlign('left')}
                  title="Align Left"
                >
                  ◀
                </button>
                <button
                  className={`se-align-btn ${(slide.narrationOverlay?.align || 'center') === 'center' ? 'se-align-active' : ''}`}
                  onClick={() => setNarrationAlign('center')}
                  title="Align Center"
                >
                  ▣
                </button>
                <button
                  className={`se-align-btn ${(slide.narrationOverlay?.align || 'center') === 'right' ? 'se-align-active' : ''}`}
                  onClick={() => setNarrationAlign('right')}
                  title="Align Right"
                >
                  ▶
                </button>
              </div>
            </div>
          )}
          <TextStyleToolbar fields={slide.narrationOverlay || {}} onChange={setNarrationStyle} />
          {slide.longDescriptionText.trim() && !slide.ttsEnabled && (
            <div className="se-hint">💡 Text-to-Speech available for this narration</div>
          )}
        </>
      )}
    </div>

    {/* Emoji - Coming Soon */}
    <div className="se-field">
      <label className="se-label">Emoji <span className="se-coming-soon">Coming Soon</span></label>
      <div className="se-emoji-preview">
        <span className="se-emoji-hint">🎉 Enhanced emoji controls with drag &amp; drop positioning</span>
      </div>
    </div>
  </>
);

export default ContentTab;
